/**
 * useStreamedBillingClaim (GDN-2618)
 *
 * Lands a working billing claim pushed over the note-generation stream onto the
 * metabar. The metabar reads `encounter.working_billing_claim`, so this patches
 * the encounters RTKQ cache directly — no refetch, codes visible the instant the
 * event arrives instead of ~19s later when the stream closes.
 *
 * PATCH, NEVER INVALIDATE. At the instant the push arrives the backend may not
 * have committed the claim yet; an invalidation would refetch a claim-less
 * encounter and clobber the codes we were just handed — which is exactly the
 * race that made a manual refresh necessary before this ticket.
 *
 * WHAT WE SKIP IS "THIS EXACT PUSH, ALREADY APPLIED" — NOT "SOME CLAIM EXISTS".
 * Those two diverge the moment a second snapshot arrives, and conflating them
 * drops real updates: regenerating a note whose encounter already has codes, or
 * any second push after our own patch made `serverClaimPresent` true. So we
 * track the identity of the claim we last patched and re-apply whenever a
 * genuinely different one shows up, whatever the cache currently holds.
 *
 * SELF-HEALING, AND QUIET WHEN THE SERVER CATCHES UP. `serverClaimPresent` still
 * earns its keep for the *same* claim: while the cached encounter has no claim
 * we keep re-applying the pushed one, so an in-flight expectation poll that
 * resolves claim-less can't leave the metabar empty. Once a fetch does return a
 * claim and we have nothing newer, we stop touching the cache — so
 * `handleStreamingComplete`'s invalidation (still the correct backstop) lands on
 * equivalent data and there is nothing to flicker.
 *
 * U3 SUPPRESSION — ANY LOCAL WRITE, NOT JUST AN EDIT. If this encounter's claim
 * was written locally this session, what is on screen owns it: the push is
 * dropped rather than merged, and the arrival transition never plays under the
 * provider's cursor. Note the predicate here is `useWorkingClaimWritten`, not
 * `useWorkingClaimEdited` — accepting the model's suggestion must still block a
 * push that would replace the accepted codes wholesale a second later, even
 * though it deliberately does NOT stand down the suggestion UI. That is the
 * whole reason the tracker distinguishes the two.
 *
 * `hasArrived` REPORTS WHAT LANDED, NOT WHAT WAS OFFERED. It only turns true
 * once a push has actually been written to the cache, so a dropped push can
 * never make `useSuggestionExpectation` highlight codes that did not change — a
 * highlight without an update is worse than no highlight. This is the same
 * discipline as the U3 path, where the suppressed arrival is consumed silently.
 *
 * INDEPENDENT DEPLOYABILITY: older backends never send the event, `claim` stays
 * null, and this hook never dispatches anything.
 */

import { useEffect, useRef, useState } from 'react';
import { useWorkingClaimWritten } from '@features/encounters/hooks/useWorkingClaimWritten';
import { encountersApi } from '@features/encounters/store/api';
import { useAppDispatch } from '@store/hooks';

import type { StreamedBillingClaim } from './useNoteGenerationStreaming';

export interface UseStreamedBillingClaimOptions {
  /** The claim pushed over the stream, or null when nothing has arrived. */
  claim?: StreamedBillingClaim | null;
  /** Encounter that owns the claim — the cache entry we patch. */
  encounterId?: string | null;
  /**
   * Whether the cached encounter currently carries a claim. Only decides
   * whether the claim we *already* applied needs re-applying; a newer push
   * always lands regardless.
   */
  serverClaimPresent?: boolean;
}

export interface StreamedBillingClaimState {
  /**
   * A pushed claim actually landed in the cache. False for a push that was
   * dropped (U3 suppression, or no encounter to patch), so the expectation UI
   * never celebrates codes that did not change.
   */
  hasArrived: boolean;
  /** The claim was already written locally; the push was dropped (U3). */
  isSuppressed: boolean;
}

export function useStreamedBillingClaim({
  claim,
  encounterId,
  serverClaimPresent = false,
}: UseStreamedBillingClaimOptions): StreamedBillingClaimState {
  const dispatch = useAppDispatch();
  const isSuppressed = useWorkingClaimWritten(encounterId);

  /** The claim object we last wrote to the cache — our "already applied" mark. */
  const appliedClaimRef = useRef<StreamedBillingClaim | null>(null);
  const [hasArrived, setHasArrived] = useState(false);

  useEffect(() => {
    if (!claim) {
      // Nothing pushed for this note — including after the stream resets on a
      // note switch, which clears any claim pushed for the previous one.
      appliedClaimRef.current = null;
      setHasArrived(false);
      return;
    }

    if (!encounterId) return;

    if (isSuppressed) {
      // Whatever the provider already has on screen outranks anything the model
      // pushes. Consume the claim so it can't land later, and report no arrival.
      appliedClaimRef.current = claim;
      return;
    }

    // Skip only when this exact claim is already applied *and* still showing.
    // A different claim is a genuine update and lands even over a cached one.
    if (appliedClaimRef.current === claim && serverClaimPresent) return;

    appliedClaimRef.current = claim;
    dispatch(
      encountersApi.util.updateQueryData(
        'getV2ProviderEncountersByEncounterId',
        { encounterId },
        draft => {
          draft.working_billing_claim = claim;
        },
      ),
    );
    setHasArrived(true);
  }, [claim, encounterId, isSuppressed, serverClaimPresent, dispatch]);

  return {
    hasArrived,
    isSuppressed,
  };
}

export default useStreamedBillingClaim;
